import { useEffect } from 'react';

import { Button, Flex, Skeleton, Table, Text } from '@mantine/core';
import { IconStar, IconX } from '@tabler/icons-react';
import { useInView } from 'react-intersection-observer';

import { useAppSelector } from '@/store/hooks';
import { useGetPatientDiagnosesInfiniteQuery } from '@/store/slices/api/patientsApiSlice';
import { useGetTransmissionReportDiagnosesInfiniteQuery } from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';

import { PatientDiagnosis } from '@/types/patient';
import { TransmissionReportDiagnosis } from '@/types/transmissionReport';

import styles from './DxCodeSelectionModal.module.scss';

interface DiagnosisCodesTableProps {
  patientId: string;
  reportId?: number;
  onMakePrimary: (diagnosis: PatientDiagnosis) => void;
  onDelete: (diagnosisId: string, diagnosisCode?: string) => void;
  isLoading?: boolean;
}

const LIST_LIMIT = 20;

const DiagnosisCodesTable = ({
  patientId,
  reportId,
  onMakePrimary,
  onDelete,
  isLoading
}: DiagnosisCodesTableProps) => {
  const { isActiveReportFinalSigned } = useAppSelector((state) => state.transmissionReportForm);

  const useTransmissionDiagnoses = isActiveReportFinalSigned && reportId;

  const patientDiagnosesQuery = useGetPatientDiagnosesInfiniteQuery(
    {
      patientId,
      limit: LIST_LIMIT
    },
    {
      skip: isActiveReportFinalSigned
    }
  );

  const transmissionDiagnosesQuery = useGetTransmissionReportDiagnosesInfiniteQuery(
    {
      reportId: reportId ?? 0,
      limit: LIST_LIMIT
    },
    {
      skip: !isActiveReportFinalSigned || !reportId
    }
  );

  const { data, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useTransmissionDiagnoses ? transmissionDiagnosesQuery : patientDiagnosesQuery;

  const allDiagnosisCodes = (data?.pages.flatMap((page) => page.data) ?? []) as
    | PatientDiagnosis[]
    | TransmissionReportDiagnosis[];

  const { ref: loadMoreRef, inView } = useInView({
    rootMargin: '50px 0px',
    threshold: 0.1,
    triggerOnce: false
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if ((isFetching && !isFetchingNextPage) || isLoading) {
    return <Skeleton height={300} mb={4} radius="none" />;
  }

  if (allDiagnosisCodes.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="md">
        No diagnosis codes found
      </Text>
    );
  }

  return (
    <Table.ScrollContainer minWidth={300} maxHeight={300}>
      <Table stickyHeader striped className={styles.table}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th className={styles.tableHeaderRow}>Code</Table.Th>
            <Table.Th className={styles.tableHeaderRow}>Description</Table.Th>
            <Table.Th className={styles.tableHeaderRow}>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {allDiagnosisCodes.map((diagnosis) => {
            return (
              <Table.Tr key={diagnosis.id}>
                <Table.Td>
                  <Text size="sm" c="plum.9" fw={600}>
                    {diagnosis.diagnosisCode}
                    {diagnosis?.isPrimary && (
                      <Text component="span" size="xs" c="plum.4" ml={4} fw={500}>
                        (Primary)
                      </Text>
                    )}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="plum">
                    {diagnosis.description}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Flex gap={'sm'} align={'center'}>
                    {/* Make Primary button: only for non-primary, not signed */}
                    {!diagnosis?.isPrimary && !isActiveReportFinalSigned && (
                      <Button
                        size="sm"
                        variant="transparent"
                        color="green"
                        p={0}
                        fw={500}
                        onClick={() => onMakePrimary(diagnosis as PatientDiagnosis)}
                        loading={isLoading}>
                        <IconStar size={14} stroke={3} />
                        <Text size={'sm'} ml={4} fw={500}>
                          Make Primary
                        </Text>
                      </Button>
                    )}
                    {/* Delete button: show for all codes if signed, otherwise only for non-primary */}
                    {(isActiveReportFinalSigned || !diagnosis?.isPrimary) && (
                      <Button
                        size="sm"
                        variant="transparent"
                        color="pink"
                        p={0}
                        fw={500}
                        onClick={() => onDelete(diagnosis.id, diagnosis.diagnosisCode)}
                        loading={isLoading}>
                        <IconX size={14} stroke={3} />
                        <Text size={'sm'} ml={4} fw={500}>
                          Delete
                        </Text>
                      </Button>
                    )}
                  </Flex>
                </Table.Td>
              </Table.Tr>
            );
          })}
          {hasNextPage && (
            <Table.Tr>
              <Table.Td colSpan={3} ref={loadMoreRef}>
                {isFetchingNextPage && <Skeleton height={40} mb={4} radius="none" />}
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
};

export default DiagnosisCodesTable;
